import { Box, Button, Center, Group, Loader, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconAlertTriangle,
  IconKey,
  IconMail,
  type IconProps,
  IconShieldLock,
} from '@tabler/icons-react';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSafeSameOriginTarget, type Session, sessionExpiriesFromLoginSuccess } from '@/api';

// The account's origin method that owns the colliding email. Drives the icon
// and the human label on the "you already have an account" screen.
const METHOD_ICON: Record<string, FunctionComponent<IconProps>> = {
  nostr: IconKey,
  email_magic_link: IconMail,
};

const AUTH_METHOD_LABELS: Record<string, string> = {
  nostr: 'Nostr',
  email_magic_link: 'email magic link',
};

export interface LoginCallbackProps {
  loginDoneCb: () => void;
  setSession: (session: Session) => void;
  apiBaseUrl: string;
  /**
   * Language-aware href to the login page. Used by the auth-method-collision
   * remedy so a logged-out user is sent to sign in (Connected Accounts requires
   * auth and would just bounce). Falls back to `#/login` when not provided.
   */
  loginHref?: string;
  /**
   * Called when the callback ends in an error, so the host page can drop its
   * "Logging in…" heading (login has failed, not succeeded).
   */
  onError?: () => void;
}

/**
 * Strip the `?refreshed=1` flag (or whichever name `useOidcRefreshOnReturn`
 * uses — defaults to `refreshed`) from both the regular query string and the
 * hash-routed query so the destination doesn't immediately re-trigger the
 * silent-refresh dance and loop the user through Hydra.
 */
function stripRefreshFlag(href: string, paramName: string = 'refreshed'): string {
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete(paramName);
    const [hashPath, hashQs] = url.hash.split('?');
    if (hashQs) {
      const hashParams = new URLSearchParams(hashQs);
      hashParams.delete(paramName);
      const rebuilt = hashParams.toString();
      url.hash = rebuilt ? `${hashPath}?${rebuilt}` : hashPath;
    }
    return url.toString();
  } catch {
    return href;
  }
}

export function LoginCallback(props: LoginCallbackProps) {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState<string[]>([]);
  // An auth-method collision isn't a failure: the email already has an account
  // via another method. We surface a calm "you already have an account" screen
  // that names that method (`method` token, `label` = null when unknown/generic)
  // and routes the user to sign in with it.
  const [collision, setCollision] = useState<{
    method: string | null;
    label: string | null;
  } | null>(null);
  const [isExchanging, setIsExchanging] = useState(false);
  const exchangeAttempted = useRef(false);

  // An error or a collision both mean login didn't complete — let the host page
  // drop its "Logging in…" heading.
  useEffect(() => {
    if (hasError.length > 0 || collision) props.onError?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, collision]);

  useEffect(() => {
    if (exchangeAttempted.current) return;
    exchangeAttempted.current = true;

    const controller = new AbortController();

    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.split('?')[1]);

    const error = hashParams.get('error');
    const errorMessage = hashParams.get('error_message');
    const originalMethod = hashParams.get('original_method');

    // A collision is not an error — the email already has an account via another
    // method. Route it to its own calm "you already have an account" screen
    // instead of the red error card below.
    if (error === 'auth_method_collision') {
      const label = originalMethod ? AUTH_METHOD_LABELS[originalMethod] : undefined;
      setCollision({ method: originalMethod || null, label: label ?? null });
      return;
    }

    if (error) {
      const errors: string[] = [];

      switch (error) {
        case 'invalid_auth_method_nostr':
          errors.push(t('login.errorInvalidAuthMethodNostr'), errorMessage || '');
          break;
        case 'access_denied':
          errors.push(t('login.errorAccessDenied'));
          break;
        case 'login_required':
        case 'consent_required':
        case 'interaction_required':
          errors.push(t('login.errorSignInAgain'));
          break;
        case 'login_failed':
          errors.push(t('login.errorTokenExchange'));
          break;
        default:
          errors.push(t('login.errorOAuthFailed'), errorMessage || error);
      }

      setHasError(errors.filter((e) => e));
      return;
    }

    const code = hashParams.get('code');
    const loginMethod = hashParams.get('login_method');
    const returnTo = hashParams.get('return_to');

    if (!code) {
      setHasError([t('login.invalidLoginResponse')]);
      return;
    }

    // Exchange authorization code for tokens. `credentials: "include"` is
    // required so the browser accepts the `Set-Cookie` carrying the opaque
    // session token (split-token sessions). The response body is a
    // `BrowserLoginSuccess` — access token only, no refresh_token.
    setIsExchanging(true);
    fetch(`${props.apiBaseUrl}/login/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 429) {
            const retryAfter = res.headers.get('Retry-After');
            throw new Error(t('login.errorRateLimited', { seconds: retryAfter || '60' }));
          }
          const body = await res.json().catch(() => ({}));
          if (body.error === 'expired_or_used_auth_code') {
            throw new Error(t('login.errorCodeExpired'));
          }
          throw new Error(body.message || t('login.errorTokenExchange'));
        }
        return res.json();
      })
      .then((data) => {
        props.setSession({
          isLoggedIn: true,
          userId: data.user_id,
          accessToken: data.access_token,
          email: data.email,
          displayName: data.display_name,
          ...sessionExpiriesFromLoginSuccess(data),
          method: loginMethod,
        } as Session);

        // When the URL fragment carries a `return_to`, honour it instead of
        // the default post-login landing. Strip `?refreshed=1` first so the
        // destination doesn't loop through useOidcRefreshOnReturn again.
        //
        // The fragment is attacker-controlled (URL fragments are never sent
        // to the backend, so the server-side origin validation that builds
        // legitimate callbacks does NOT apply here). Re-validate same-origin
        // client-side to block `javascript:`, `data:`, off-origin redirects.
        if (returnTo) {
          const stripped = stripRefreshFlag(returnTo);
          if (isSafeSameOriginTarget(stripped)) {
            window.location.assign(stripped);
            return;
          }
          console.warn(
            'LoginCallback: refusing unsafe return_to; falling back to default landing',
            { returnTo }
          );
        }

        props.loginDoneCb();
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setIsExchanging(false);
        setHasError([err.message || t('login.errorTokenExchange')]);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Collision: "you already have an account". The method that owns the email is
  // the hero — its icon and name are the one memorable element, and the primary
  // action names it so the user knows exactly which door to use.
  const CollisionCard = () => {
    const label = collision?.label ?? null;
    const MethodIcon = (collision?.method && METHOD_ICON[collision.method]) || IconShieldLock;
    return (
      <Stack align="center" gap="lg" ta="center">
        <ThemeIcon size={64} radius="xl" variant="light" color="primary">
          <MethodIcon size={30} stroke={1.5} />
        </ThemeIcon>

        <Stack gap={6}>
          <Title order={3} fw={600}>
            {t('login.callbackAccountExistsTitle', 'You already have an account')}
          </Title>
          <Text size="sm" c="dimmed">
            {label
              ? t('login.callbackAccountExistsBody', "Welcome back — you've signed in here before.")
              : t(
                  'login.callbackAccountExistsBodyGeneric',
                  'This email signs in with a different method.'
                )}
          </Text>
        </Stack>

        {label && (
          <Stack gap={8} align="center">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.07em' }}>
              {t('login.callbackMethodEyebrow', 'Your sign-in method')}
            </Text>
            <Group
              gap={8}
              px="md"
              py={6}
              wrap="nowrap"
              style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 'var(--mantine-radius-xl)',
              }}
            >
              <MethodIcon size={16} stroke={1.6} />
              <Text size="sm" fw={500} tt="capitalize">
                {label}
              </Text>
            </Group>
          </Stack>
        )}

        <Stack gap={8} w="100%">
          <Button component="a" href={props.loginHref || '#/login'} fullWidth size="md">
            {label
              ? t('login.callbackContinueWith', 'Continue with {{method}}', { method: label })
              : t('login.errorAuthMethodCollisionRemedyLink', 'Go to sign in')}
          </Button>
          <Text size="xs" c="dimmed">
            {t(
              'login.callbackAddSsoNote',
              'You can add single sign-on later from Connected Accounts.'
            )}
          </Text>
        </Stack>
      </Stack>
    );
  };

  // Genuine failures (bad state, token exchange, rate limit) stay in an error
  // register — red, because something actually went wrong.
  const ErrorCard = () => (
    <Stack align="center" gap="lg" ta="center">
      <ThemeIcon size={64} radius="xl" variant="light" color="red">
        <IconAlertTriangle size={30} stroke={1.5} />
      </ThemeIcon>
      <Stack gap={6}>
        <Title order={3} fw={600}>
          {t('login.callbackErrorTitle', "Couldn't sign you in")}
        </Title>
        {hasError.map((error, index) => (
          <Text key={index} size="sm" c={index === 0 ? undefined : 'dimmed'}>
            {error}
          </Text>
        ))}
      </Stack>
      <Button
        component="a"
        href={props.loginHref || '#/login'}
        variant="light"
        color="gray"
        fullWidth
        size="md"
      >
        {t('login.callbackBackToSignIn', 'Back to sign in')}
      </Button>
    </Stack>
  );

  return (
    <Box maw={360} mx="auto">
      {isExchanging && (
        <Center>
          <Stack align="center" gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {t('login.loggingIn')}
            </Text>
          </Stack>
        </Center>
      )}
      {collision && <CollisionCard />}
      {hasError.length > 0 && <ErrorCard />}
    </Box>
  );
}
